// Wallet picker for Solana networks. Looks like the EVM (web3modal) picker:
// a centred card with one tile per wallet. Phantom is the only wallet the
// platform can sign with today; Solflare and Backpack are shown blurred with a
// "Coming soon" tag (a tag, not a button - they cannot be clicked).
//
// pickSolanaWallet() resolves with 'Phantom' when the user picks it, or null when
// the modal is closed. Plain DOM (like the web3modal card) so it works from any
// caller without a React provider.

const STYLE_ID = 'ee-sol-wallet-modal-style';
const ROOT_ID = 'ee-sol-wallet-modal';

const CSS = `
#${ROOT_ID}{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;
  background:rgba(15,18,28,.55);animation:eeSolFade .15s ease-out}
#${ROOT_ID} .ee-sol-card{position:relative;width:min(560px,92vw);background:#fff;border-radius:14px;
  padding:34px 22px 22px;box-shadow:0 18px 60px rgba(0,0,0,.35);font-family:inherit}
#${ROOT_ID} .ee-sol-title{margin:0 0 14px;text-align:center;font-size:15px;font-weight:600;color:#1c2233}
#${ROOT_ID} .ee-sol-close{position:absolute;top:6px;right:12px;border:0;background:none;font-size:26px;
  line-height:1;color:#8a90a2;cursor:pointer}
#${ROOT_ID} .ee-sol-close:hover{color:#1c2233}
#${ROOT_ID} .ee-sol-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
@media (max-width:520px){#${ROOT_ID} .ee-sol-grid{grid-template-columns:1fr}}
#${ROOT_ID} .ee-sol-tile{position:relative;display:flex;flex-direction:column;align-items:center;
  text-align:center;padding:18px 10px 16px;border:1px solid #e6e8ef;border-radius:12px;background:#fff;
  color:#1c2233;text-decoration:none;cursor:pointer;transition:box-shadow .15s,transform .15s}
#${ROOT_ID} button.ee-sol-tile:hover{box-shadow:0 6px 20px rgba(80,60,200,.18);transform:translateY(-2px)}
#${ROOT_ID} .ee-sol-icon{width:56px;height:56px;border-radius:14px;display:flex;align-items:center;
  justify-content:center;margin-bottom:10px}
#${ROOT_ID} .ee-sol-name{font-size:16px;font-weight:600}
#${ROOT_ID} .ee-sol-desc{font-size:12px;color:#6b7186;margin-top:4px}
#${ROOT_ID} .ee-sol-tile.soon{cursor:not-allowed;background:#fafbfd}
#${ROOT_ID} .ee-sol-tile.soon .ee-sol-icon,#${ROOT_ID} .ee-sol-tile.soon .ee-sol-name,
#${ROOT_ID} .ee-sol-tile.soon .ee-sol-desc{filter:blur(2.2px);opacity:.55;user-select:none}
#${ROOT_ID} .ee-sol-tag{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);white-space:nowrap;
  padding:4px 10px;border-radius:999px;background:#eef0ff;color:#4b3fd6;border:1px solid #d5d9ff;
  font-size:11px;font-weight:700;letter-spacing:.3px;pointer-events:none}
@keyframes eeSolFade{from{opacity:0}to{opacity:1}}
`;

const ICONS = {
  Phantom:
    '<svg width="34" height="34" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#AB9FF2"/>' +
    '<path fill="#fff" d="M32 14C21 14 12 23 12 34c0 3 2 5 4.500 5 4.500 0 6-4 9-4s4 4 6.500 4 3.500-4 6.500-4 4.500 4 9 4c2.500 0 4.500-2 4.500-5C52 23 43 14 32 14z"/>' +
    '<circle cx="26" cy="29" r="3" fill="#AB9FF2"/><circle cx="37" cy="29" r="3" fill="#AB9FF2"/></svg>',
  MetaMask:
    '<svg width="34" height="34" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#FFF3E6"/>' +
    '<path fill="#E2761B" d="M10 10l19 14-3-8zM54 10L35 24l3-8zM12 40l6 10 8-2-3-9zM52 40l-6 10-8-2 3-9zM26 33l-2 6 8 1v-8zM38 33l2 6-8 1v-8z"/>' +
    '<path fill="#F6851B" d="M10 10l16 6 3 8-3 9-11-3zM54 10l-16 6-3 8 3 9 11-3zM23 40l9 1 9-1-3 9h-12z"/></svg>',
  WalletConnect:
    '<svg width="34" height="34" viewBox="0 0 64 64"><circle cx="32" cy="32" r="28" fill="#3B99FC"/>' +
    '<path fill="#fff" d="M19 28c7-7 19-7 26 0l1 1-3 3-1-1c-5-5-14-5-19 0l-1 1-3-3zm4 8c4-4 14-4 18 0l-9 9z"/></svg>',
  'XDC Pay':
    '<svg width="34" height="34" viewBox="0 0 64 64"><circle cx="32" cy="32" r="29" fill="#2A4A8A" stroke="#c9d3e8" stroke-width="3"/>' +
    '<path d="M22 20l20 24M42 20L22 44" stroke="#fff" stroke-width="6" stroke-linecap="round"/></svg>',
  Solflare:
    '<svg width="34" height="34" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#FFEF46"/>' +
    '<path d="M32 12l6 14 14 6-14 6-6 14-6-14-14-6 14-6z" fill="#1c2233"/></svg>',
  Backpack:
    '<svg width="34" height="34" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#E33E3F"/>' +
    '<rect x="20" y="22" width="24" height="26" rx="8" fill="#fff"/><rect x="26" y="14" width="12" height="9" rx="4" fill="none" stroke="#fff" stroke-width="3"/>' +
    '<rect x="25" y="34" width="14" height="5" rx="2.500" fill="#E33E3F"/></svg>',
};

const FAMILIES = {
  solana: {
    title: 'Connect a Solana wallet',
    tiles: [
      { id: 'Phantom', name: 'Phantom', desc: 'Connect with Phantom', active: true,
        installed: () => typeof window !== 'undefined' && !!((window.phantom && window.phantom.solana && window.phantom.solana.isPhantom) || (window.solana && window.solana.isPhantom)),
        installUrl: 'https://phantom.app/download' },
      { id: 'Solflare', name: 'Solflare', desc: 'Connect with Solflare', active: false },
      { id: 'Backpack', name: 'Backpack', desc: 'Connect with Backpack', active: false },
    ],
  },
  evm: {
    title: 'Connect a wallet',
    tiles: [
      { id: 'injected', name: 'MetaMask', desc: 'Connect to your MetaMask Wallet', active: true,
        installed: () => typeof window !== 'undefined' && !!window.ethereum,
        installUrl: 'https://metamask.io/download/' },
      { id: 'walletconnect', name: 'WalletConnect', desc: 'Scan with WalletConnect to connect', active: true },
      { id: 'custom-xdc', name: 'XDC Pay', desc: 'Connect with XDC Pay', active: true },
    ],
  },
};

let pending = null;


const pick = (family, logos) => {
  logos = logos || {};
  const cfg = FAMILIES[family];
  if (typeof document === 'undefined') return Promise.resolve(cfg.tiles[0].id);
  if (pending) return pending;

  pending = new Promise((resolve) => {
    if (!document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }

    const root = document.createElement('div');
    root.id = ROOT_ID;

    const tiles = cfg.tiles.map((w) => {
      const missing = w.active && w.installed && !w.installed();
      const inner =
        '<div class="ee-sol-icon">' + (logos[w.id] ? '<img src="' + String(logos[w.id]).replace(/"/g, '') + '" alt="" style="width:44px;height:44px;object-fit:contain"/>' : ICONS[w.name]) + '</div>' +
        '<div class="ee-sol-name">' + w.name + '</div>' +
        '<div class="ee-sol-desc">' + (missing ? 'Not installed - click to get it' : w.desc) + '</div>';
      if (!w.active) {
        return '<div class="ee-sol-tile soon" aria-disabled="true">' + inner + '<span class="ee-sol-tag">Coming soon</span></div>';
      }
      if (missing) {
        return '<a class="ee-sol-tile" href="' + w.installUrl + '" target="_blank" rel="noopener noreferrer">' + inner + '</a>';
      }
      return '<button type="button" class="ee-sol-tile" data-wallet="' + w.id + '">' + inner + '</button>';
    }).join('');

    root.innerHTML =
      '<div class="ee-sol-card" role="dialog" aria-modal="true" aria-label="' + cfg.title + '">' +
      '<button type="button" class="ee-sol-close" aria-label="Close">&times;</button>' +
      '<h4 class="ee-sol-title">' + cfg.title + '</h4>' +
      '<div class="ee-sol-grid">' + tiles + '</div></div>';

    const done = (value) => {
      document.removeEventListener('keydown', onKey, true);
      if (root.parentNode) root.parentNode.removeChild(root);
      pending = null;
      resolve(value);
    };
    const onKey = (e) => { if (e.key === 'Escape') done(null); };

    root.addEventListener('click', (e) => {
      if (e.target === root || (e.target.closest && e.target.closest('.ee-sol-close'))) return done(null);
      const tile = e.target.closest && e.target.closest('[data-wallet]');
      if (tile) done(tile.getAttribute('data-wallet'));
    });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(root);
  });
  return pending;
};

export const pickSolanaWallet = () => pick('solana');
// Resolves with a web3modal provider id: 'injected' (MetaMask), 'walletconnect' or 'custom-xdc'.
export const pickEvmWallet = (logos) => pick('evm', logos);